import React, { memo } from "react";
import { DeleteIcon, RepeatClockIcon } from "@chakra-ui/icons";
import { Box } from "@chakra-ui/layout";
import { HStack, IconButton, SkeletonText, Tooltip } from "@chakra-ui/react";
import { Table, Tbody, Td, Th, Tr, Thead } from "@chakra-ui/table";
import { Tag } from "@chakra-ui/tag";
import { FiCopy } from "react-icons/fi";
import { CherryHubAdminApi } from "../../api/adminApiModels";
import DateUtil from "../../util/DateUtil";

interface RegistrationFormConfigurationTableProps {
  registrations:
    | CherryHubAdminApi.RegistrationFormConfigurationSummaryItem[]
    | null;
  isLoading: boolean;
  debouncedQuery: string;
  disableActions?: boolean;
  onClickEditRegistration: (
    registration: CherryHubAdminApi.RegistrationFormConfigurationSummaryItem
  ) => void;
  onClickCloneRegistration: (
    registration: CherryHubAdminApi.RegistrationFormConfigurationSummaryItem
  ) => void;
  onClickDeleteRegistration: (
    registration: CherryHubAdminApi.RegistrationFormConfigurationSummaryItem
  ) => void;
  onClickRestoreRegistration: (
    registration: CherryHubAdminApi.RegistrationFormConfigurationSummaryItem
  ) => void;
}

export const RegistrationFormConfigurationsTable = memo(
  ({
    registrations,
    isLoading,
    disableActions,
    debouncedQuery,
    onClickEditRegistration,
    onClickCloneRegistration,
    onClickDeleteRegistration,
    onClickRestoreRegistration,
  }: RegistrationFormConfigurationTableProps) => {
    return (
      <Box w="100%" overflowX="auto">
        <Table size="sm">
          <Thead>
            <Tr>
              <Th width="0.1%" whiteSpace="nowrap">
                Registration #
              </Th>
              <Th>Name</Th>
              <Th>Status</Th>
              <Th>Created</Th>
              <Th>Last Modified</Th>
              <Th></Th>
            </Tr>
          </Thead>
          <Tbody>
            {!isLoading &&
              (registrations ?? []).map((registration) => (
                <Tr key={registration.Meta?.Id}>
                  <Td textAlign="center" width="0.1%" whiteSpace="nowrap">
                    {registration.RegistrationFormConfigurationNo}
                  </Td>
                  <Td
                    title="Edit digital membership registration form."
                    _hover={
                      !registration.Meta?.IsDeleted
                        ? { textDecoration: "underline", cursor: "pointer" }
                        : {}
                    }
                    onClick={() =>
                      !registration.Meta?.IsDeleted &&
                      onClickEditRegistration(registration)
                    }
                  >
                    {!!registration.Meta?.IsDeleted && (
                      <Tag size="sm" mr="2">
                        Deleted
                      </Tag>
                    )}
                    {registration.DisplayName}
                  </Td>
                  <Td width="0.1%" whiteSpace="nowrap">
                    <Tag
                      size="sm"
                      bgColor={registration.IsEnabled ? "" : "cherryButton.500"}
                      color={registration.IsEnabled ? "" : "white"}
                    >
                      {registration.IsEnabled ? "Enabled" : "Disabled"}
                    </Tag>
                  </Td>
                  <Td width="0.1%" whiteSpace="nowrap">
                    {DateUtil.getLocalDateFormatFromString(
                      registration.Meta?.DateCreated
                    )}
                  </Td>
                  <Td width="0.1%" whiteSpace="nowrap">
                    {DateUtil.getLocalDateFormatFromString(
                      registration.Meta?.DateUpdated
                    )}
                  </Td>
                  <Td width="0.1%" whiteSpace="nowrap">
                    <HStack spacing={2} justifyContent="center">
                      {(registration.Meta?.IsDeleted && (
                        <IconButton
                          aria-label="Restore digital membership registration form."
                          title="Restore digital membership registration form."
                          icon={<RepeatClockIcon />}
                          size="xs"
                          isDisabled={disableActions}
                          onClick={() =>
                            onClickRestoreRegistration(registration)
                          }
                        />
                      )) || (
                        <>
                          <Tooltip
                            label={`Clone "${registration.DisplayName}"`}
                            placement="top"
                          >
                            <IconButton
                              aria-label={`Clone "${registration.DisplayName}"`}
                              icon={<FiCopy />}
                              size="xs"
                              isDisabled={disableActions}
                              onClick={() =>
                                onClickCloneRegistration(registration)
                              }
                            />
                          </Tooltip>
                          <Tooltip
                            label={`Delete "${registration.DisplayName}"`}
                            placement="top"
                          >
                            <IconButton
                              marginLeft="1"
                              aria-label={`Delete "${registration.DisplayName}"`}
                              icon={<DeleteIcon />}
                              size="xs"
                              isDisabled={disableActions}
                              onClick={() =>
                                onClickDeleteRegistration(registration)
                              }
                            />
                          </Tooltip>
                        </>
                      )}
                    </HStack>
                  </Td>
                </Tr>
              ))}
            {registrations?.length === 0 && !isLoading && (
              <Tr>
                <Td colSpan={5} textAlign="center">
                  No regisration form configuration found matching query '
                  {debouncedQuery}'
                </Td>
              </Tr>
            )}
            {isLoading && (
              <Tr>
                <Td width="5%">
                  <SkeletonText noOfLines={12} spacing="4" width="100%" />
                </Td>
                <Td width="65%">
                  <SkeletonText noOfLines={12} spacing="4" width="100%" />
                </Td>
                <Td width="5%" whiteSpace="nowrap">
                  <SkeletonText noOfLines={12} spacing="4" width="100%" />
                </Td>
                <Td width="5%" whiteSpace="nowrap">
                  <SkeletonText noOfLines={12} spacing="4" width="100%" />
                </Td>
                <Td width="10%" whiteSpace="nowrap">
                  <SkeletonText noOfLines={12} spacing="4" width="100%" />
                </Td>
                <Td width="10%" whiteSpace="nowrap">
                  <SkeletonText noOfLines={12} spacing="4" width="100%" />
                </Td>
              </Tr>
            )}
          </Tbody>
        </Table>
      </Box>
    );
  }
);
